import React, { useState, forwardRef, useImperativeHandle } from 'react';
import Modal from '../Modal';
import useToast from '../../../hooks/useToast';
import { useUserStore } from '../../../store/user';
import { signaturesService } from '../../../services/signatures.service';
import { applySignatureToPdf } from '../../../utils/pdfSignatureUtils';

export interface ModalSignGovBrProps {
  documentId?: string;
  documentName?: string;
  documentBlob?: ArrayBuffer | null;
  onSuccess?: () => void;
}

export interface ModalSignGovBrHandle {
  openModal: (docId?: string, blob?: ArrayBuffer) => void;
  closeModal: () => void;
}

const ModalSignGovBr: React.ForwardRefRenderFunction<ModalSignGovBrHandle, ModalSignGovBrProps> = (
  { documentId, documentName, documentBlob, onSuccess },
  ref
) => {
  const modalRef = React.useRef<any>(null);
  const toast = useToast();
  const { user } = useUserStore();

  const [activeDocId, setActiveDocId] = useState<string | undefined>(documentId);
  const [activeBlob, setActiveBlob] = useState<ArrayBuffer | null | undefined>(documentBlob);

  const [signerName, setSignerName] = useState(user?.name || '');
  const [signerCpf, setSignerCpf] = useState('***.123.456-**');
  const [signerRole, setSignerRole] = useState('Servidor Público / Cidadão');
  const [certLevel, setCertLevel] = useState<'govbr_prata' | 'govbr_ouro'>('govbr_ouro');
  const [manifestPage, setManifestPage] = useState(false);
  const [loading, setLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal(docId, blob) {
      if (docId) setActiveDocId(docId);
      if (blob) setActiveBlob(blob);
      if (user?.name) setSignerName(user.name);
      modalRef.current?.openModal();
    },
    closeModal() {
      modalRef.current?.closeModal();
    },
  }));

  const handleSign = async () => {
    if (!signerName.trim()) {
      toast.errorToast('Informe o nome do signatário');
      return;
    }

    const docIdToSign = activeDocId || documentId;
    if (!docIdToSign) {
      toast.errorToast('Identificador do documento não fornecido');
      return;
    }

    try {
      setLoading(true);

      const randomAuth = `GED-BR-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;

      let signedFile: File | undefined = undefined;

      const blobToProcess = activeBlob || documentBlob;
      if (blobToProcess) {
        // Aplica o carimbo visual no PDF usando pdf-lib
        const signedPdfBytes = await applySignatureToPdf(blobToProcess, {
          signerName,
          signerCpf,
          signerRole,
          certificateType: certLevel,
          authCode: randomAuth,
          manifestPage,
        });

        const blob = new Blob([signedPdfBytes], { type: 'application/pdf' });
        signedFile = new File([blob], documentName || 'documento_assinado_govbr.pdf', {
          type: 'application/pdf',
        });
      }

      await signaturesService.signGovBr(
        docIdToSign,
        {
          signerName,
          signerCpf,
          signerRole,
          certificateLevel: certLevel,
        },
        signedFile
      );

      toast.successToast('Documento assinado com sucesso via Gov.br!');
      modalRef.current?.closeModal();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Erro na assinatura Gov.br:', error);
      toast.errorToast(error?.response?.data?.message || 'Erro ao processar assinatura eletrônica Gov.br');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Assinatura Eletrônica Oficial (Gov.br)" ref={modalRef} maxWidth={520}>
      <div style={{ padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Banner Oficial Gov.br */}
        <div
          style={{
            background: 'linear-gradient(135deg, #002244 0%, #005b96 100%)',
            padding: '16px',
            borderRadius: '8px',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '20px' }}>🇧🇷</span>
              <span style={{ fontSize: '16px', fontWeight: 800, letterSpacing: '0.05em' }}>gov.br</span>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#cbd5e1' }}>
              Decreto nº 10.543/2020 e Lei Federal nº 14.063/2020
            </p>
          </div>
          <span
            style={{
              background: certLevel === 'govbr_ouro' ? '#f59e0b' : '#94a3b8',
              color: 'white',
              fontSize: '11px',
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: '16px',
              textTransform: 'uppercase',
            }}
          >
            {certLevel === 'govbr_ouro' ? '★ Nível Ouro' : '◈ Nível Prata'}
          </span>
        </div>

        {/* Formulário do Signatário */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
              Nome do Signatário
            </label>
            <input
              type="text"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Nome completo do signatário"
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                CPF do Titular
              </label>
              <input
                type="text"
                value={signerCpf}
                onChange={(e) => setSignerCpf(e.target.value)}
                placeholder="000.000.000-00"
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                Selo de Confiabilidade
              </label>
              <select
                value={certLevel}
                onChange={(e) => setCertLevel(e.target.value as any)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                  background: '#fff',
                }}
              >
                <option value="govbr_ouro">Ouro (Validação Biométrica / Bancária)</option>
                <option value="govbr_prata">Prata (Validação via Internet Banking)</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
              Cargo / Função / Lotação
            </label>
            <input
              type="text"
              value={signerRole}
              onChange={(e) => setSignerRole(e.target.value)}
              placeholder="Ex: Secretário Municipal, Fiscal de Contrato, Pregoeiro"
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Formato de Chancela */}
          <div
            style={{
              padding: '12px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
              Opções de Representação Visual no PDF
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
              <input
                type="radio"
                name="stampType"
                checked={!manifestPage}
                onChange={() => setManifestPage(false)}
              />
              <span>Carimbo Oficial no Rodapé da última página (padrão)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
              <input
                type="radio"
                name="stampType"
                checked={manifestPage}
                onChange={() => setManifestPage(true)}
              />
              <span>Anexar Folha Oficial de Manifesto de Assinaturas (PAdES)</span>
            </label>
          </div>
        </div>

        {/* Botão de Ação */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
          <button
            type="button"
            onClick={() => modalRef.current?.closeModal()}
            style={{
              padding: '10px 18px',
              borderRadius: 6,
              border: '1px solid #cbd5e1',
              background: '#f1f5f9',
              color: '#475569',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSign}
            disabled={loading}
            style={{
              padding: '10px 22px',
              borderRadius: 6,
              border: 'none',
              background: '#005b96',
              color: 'white',
              fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 2px 4px rgba(0, 91, 150, 0.3)',
            }}
          >
            {loading ? 'Processando Assinatura...' : '🇧🇷 Assinar com Gov.br'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(ModalSignGovBr);
